import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, ExternalLink, Mail, MapPin, Phone, Send } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { API, CONTACT } from "@/lib/content";

const SERVICE_OPTIONS = ["Personal Tax", "Corporate Tax", "Accounting", "CRA Assistance", "Business Advisory", "Other"];

const initialForm = { name: "", email: "", phone: "", company: "", service: "", message: "", website: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const renderedAt = useRef(Date.now());
  const mapRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, { ...form, ts: renderedAt.current / 1000 });
      if (data.stored) {
        toast.success("Thank you — your message has been received. We will be in touch shortly.");
        setForm(initialForm);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call our office instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
              Let's talk about your tax &amp; accounting needs.
            </h2>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col">
              <address className="not-italic">
                <p className="font-display text-2xl font-semibold text-[#102036]">{CONTACT.firm}</p>
                <ul className="mt-6 space-y-5 text-[15px] text-[#4A5568]">
                  <li className="flex gap-4">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#3682AE]" aria-hidden="true" />
                    <span data-testid="contact-address">
                      {CONTACT.addressLines[0]}<br />{CONTACT.addressLines[1]}<br />{CONTACT.addressLines[2]}
                    </span>
                  </li>
                  <li>
                    <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex gap-4 transition-colors hover:text-[#102036]">
                      <Phone className="mt-0.5 h-5 w-5 shrink-0 text-[#3682AE]" aria-hidden="true" />
                      <span className="font-semibold text-[#102036]">{CONTACT.phoneDisplay}</span>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex gap-4 transition-colors hover:text-[#102036]">
                      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#3682AE]" aria-hidden="true" />
                      <span className="font-semibold text-[#102036]">{CONTACT.email}</span>
                    </a>
                  </li>
                  <li className="flex gap-4">
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[#3682AE]" aria-hidden="true" />
                    <span data-testid="contact-hours">
                      {CONTACT.hours.map((h) => (
                        <span key={h.days} className="block">
                          <span className="font-semibold text-[#102036]">{h.days}</span> — {h.time}
                        </span>
                      ))}
                    </span>
                  </li>
                </ul>
              </address>

              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={CONTACT.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-get-directions-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#102036] px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#102036]"
                >
                  Get directions <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-us-mail-button"
                  className="inline-flex items-center gap-2 rounded-full border border-[#102036]/20 px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#102036]"
                >
                  Email us <Mail className="h-4 w-4" />
                </a>
              </div>

              <div ref={mapRef} className="mt-10 flex-1 overflow-hidden rounded-lg border border-[#102036]/10" data-testid="contact-map">
                <iframe
                  title="Map — Ford & Associates, 17665 Leslie Street, Unit 35, Newmarket, Ontario"
                  src={CONTACT.mapEmbed}
                  className="h-[280px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-lg border border-[#102036]/10 bg-[#FFFFFF] p-8 shadow-[0_8px_30px_rgb(12,24,42,0.05)] sm:p-10"
              noValidate={false}
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label htmlFor="cf-name" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Name *</label>
                  <input id="cf-name" required maxLength={120} value={form.name} onChange={set("name")} data-testid="contact-form-name" className="field-input" autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Email *</label>
                  <input id="cf-email" type="email" required maxLength={200} value={form.email} onChange={set("email")} data-testid="contact-form-email" className="field-input" autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Telephone</label>
                  <input id="cf-phone" type="tel" maxLength={40} value={form.phone} onChange={set("phone")} data-testid="contact-form-phone" className="field-input" autoComplete="tel" />
                </div>
                <div>
                  <label htmlFor="cf-company" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Company (if applicable)</label>
                  <input id="cf-company" maxLength={160} value={form.company} onChange={set("company")} data-testid="contact-form-company" className="field-input" autoComplete="organization" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-service" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Service Needed *</label>
                  <select id="cf-service" required value={form.service} onChange={set("service")} data-testid="contact-form-service" className="field-input appearance-none">
                    <option value="" disabled>Select a service…</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Message *</label>
                  <textarea id="cf-message" required rows={5} maxLength={5000} value={form.message} onChange={set("message")} data-testid="contact-form-message" className="field-input resize-y" />
                </div>
              </div>

              <div className="hp-field" aria-hidden="true">
                <label htmlFor="cf-website">Website</label>
                <input id="cf-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#102036] px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#102036] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Sending…" : "Send message"}
                <Send className="h-4 w-4" />
              </button>
              <p className="mt-5 text-xs leading-relaxed text-[#4A5568]/80">
                Please do not include sensitive tax information (SIN, account numbers, documents) in this form.
                Secure document exchange happens through Client Manager.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
